import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import API from "@/lib/api";
import SeoPageLayout from "@/components/SeoPageLayout";
import SeoEntitySummary from "@/components/SeoEntitySummary";
import RateLockBadge from "@/components/RateLockBadge";
import { ArrowRight, CheckCircle2, DollarSign, Truck } from "lucide-react";

/**
 * /pricing-vs-ro-vs-hers-vs-henry-meds — cash-price comparison hub.
 * Rows loaded from the admin-editable /api/competitors endpoint so the
 * table stays current when competitor prices change. FALLBACK ships
 * the initial content in case the API is unreachable.
 */
const FALLBACK = [
  { name: "GLPFlo (compounded semaglutide)", monthly_price: "$162", consult: "Included", shipping: "Included · 2-day cold-chain", cancel: "Anytime, no fees", pharmacy: "Polomar Specialty Pharmacy (US 503A)", editors_pick: true },
  { name: "GLPFlo (compounded tirzepatide)", monthly_price: "$199", consult: "Included", shipping: "Included · 2-day cold-chain", cancel: "Anytime, no fees", pharmacy: "Polomar Specialty Pharmacy (US 503A)" },
  { name: "Ro Body", monthly_price: "$99 membership + Rx cost", consult: "Bundled", shipping: "Varies", cancel: "Monthly", pharmacy: "Varies by state" },
  { name: "Hims & Hers", monthly_price: "$199+/mo", consult: "Included", shipping: "Standard", cancel: "Monthly", pharmacy: "Third-party network" },
  { name: "WeightWatchers Clinic (Sequence)", monthly_price: "$99 membership + insurance cost", consult: "Included", shipping: "Varies", cancel: "Monthly", pharmacy: "Retail (via insurance)" },
  { name: "Henry Meds", monthly_price: "$296/mo compounded", consult: "Included", shipping: "Included", cancel: "Monthly", pharmacy: "Compounding partner" },
  { name: "Found", monthly_price: "$99 membership + Rx cost", consult: "Bundled", shipping: "Varies", cancel: "Monthly", pharmacy: "Retail / compounding" },
  { name: "Mochi Health", monthly_price: "Membership + Rx cost", consult: "Included", shipping: "Included", cancel: "Monthly", pharmacy: "Compounding partner" },
];

export default function PricingMegaComparePage() {
  const [rows, setRows] = useState(FALLBACK);
  useEffect(() => {
    API.get("/competitors")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setRows(data);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const canonical = "https://glpflo.org/pricing-vs-ro-vs-hers-vs-henry-meds";
  const title = "GLP-1 Telehealth Pricing Compared: GLPFlo vs Ro vs Hers vs Henry Meds vs Sequence";
  const description = "Which telehealth service has the cheapest compounded GLP-1? Full cash-price comparison of GLPFlo, Ro, Hims & Hers, Henry Meds, Sequence, Found, and Mochi Health — updated 2026.";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://glpflo.org/" },
          { "@type": "ListItem", "position": 2, "name": "Pricing comparison", "item": canonical },
        ],
      },
      {
        "@type": "Product",
        "name": "Compounded semaglutide (GLPFlo)",
        "description": "Flat monthly price includes physician consult, prescription, medication, and 2-day cold-chain shipping.",
        "brand": { "@type": "Brand", "name": "GLPFlo" },
        "offers": {
          "@type": "Offer",
          "priceCurrency": "USD",
          "price": "162.00",
          "availability": "https://schema.org/InStock",
          "url": "https://glpflo.org/pricing",
        },
      },
    ],
  };

  return (
    <SeoPageLayout seo={{ title, description, canonical, jsonLd }}>
      <SeoEntitySummary
        heading="GLP-1 telehealth pricing — quick facts"
        entries={rows.map((r) => ({ label: r.name, value: `${r.monthly_price} · ${r.shipping}` }))}
      />
      <section className="bg-gradient-to-br from-purple-50 to-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 text-purple-700 bg-purple-100 rounded-full px-3 py-1 text-xs font-semibold mb-4">
            <DollarSign className="w-3.5 h-3.5" /> Full comparison · Updated 2026
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLPFlo vs Ro vs Hers vs Henry Meds &amp; more
          </h1>
          <p className="text-lg text-slate-700 leading-relaxed max-w-3xl">
            An honest side-by-side of every major GLP-1 telehealth in the US. All prices are the cash prices published by each provider (or verified with a mystery-shopper enrolment) as of February 2026.
          </p>
          <div className="mt-6 flex">
            <RateLockBadge testid="rate-lock-mega-compare" />
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-10">
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Provider</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Monthly price</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Consult</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Shipping</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Cancel</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Pharmacy</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || r.name} className={`${i % 2 === 0 ? "bg-white" : "bg-slate-50/50"} ${r.editors_pick ? "ring-2 ring-emerald-200" : ""}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {r.name} {r.editors_pick && <span className="ml-2 text-[10px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full uppercase tracking-wide">Editor&apos;s pick</span>}
                  </td>
                  <td className="px-4 py-3 text-purple-700 font-semibold">{r.monthly_price}</td>
                  <td className="px-4 py-3 text-slate-700">{r.consult}</td>
                  <td className="px-4 py-3 text-slate-700">{r.shipping}</td>
                  <td className="px-4 py-3 text-slate-700">{r.cancel}</td>
                  <td className="px-4 py-3 text-slate-700">{r.pharmacy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Why GLPFlo wins on price</h2>
          <ul className="space-y-2 text-slate-700">
            <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" /> Flat rate includes consult + Rx + medication + shipping — no add-ons or membership fees stacked on top.</li>
            <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" /> No insurance billing means no surprise co-pays or &quot;prior authorisation&quot; delays.</li>
            <li className="flex gap-2"><Truck className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" /> 2-day cold-chain shipping baked into the price — competitors typically charge extra or use non-cold shipping.</li>
            <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" /> Cancel anytime, no cancellation fees.</li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/register" className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-6 py-3 text-white font-semibold hover:bg-purple-700 transition">Get started at $162/mo <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/where-to-order-glp1-online" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 font-semibold text-slate-700 hover:bg-slate-50 transition">See full ordering guide</Link>
          </div>
        </section>
      </div>
    </SeoPageLayout>
  );
}
